import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { environment } from '../../../../environments/environment';
import { LegacyAuthSessionService } from '../../auth/legacy-auth-session.service';
import { SessionStorageService } from '../../storage/session-storage.service';
import { LocalStorageService } from '../../storage/local-storage.service';
import { toTrimmedString } from '../../../shared/utils/coerce.utils';
import { BackendRoutingService } from '../backend-routing/backend-routing.service';
import { AuthStore } from '../../stores/auth.store';
import { ClassStore } from '../../stores/class.store';
import { UserStore } from '../../stores/user.store';
import { BulkLogApiService } from './bulk-log-api.service';
import type { BulkLogBatch, BulkLogEntry, BulkLogOptions, PendingBulkLog } from './bulk-log.model';
import { isKnownLegacyVerb, isLearnerRecordVerb } from './legacy-verb-map';

const FLUSH_INTERVAL_MS = 60_000;
const CALLER_APP_TYPE_WEB_CLIENT = 4;
const EVENT_TYPE_LRS = 'LRS';
const EVENT_TYPE_SYS = 'SYS';
const ADMIN_ROLES = new Set(['SA', 'A', 'PA', 'PU', 'DA', 'SCA', 'PS', 'PSC', 'CVM', 'PRU']);

@Injectable({ providedIn: 'root' })
export class BulkLogService {
  private readonly api = inject(BulkLogApiService);
  private readonly routing = inject(BackendRoutingService);
  private readonly legacyAuth = inject(LegacyAuthSessionService);
  private readonly auth = inject(AuthStore);
  private readonly classStore = inject(ClassStore);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly userStore = inject(UserStore);
  private readonly document = inject(DOCUMENT, { optional: true });

  private readonly buffer: PendingBulkLog[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private started = false;
  private flushInFlight: Promise<boolean> | null = null;

  private readonly onTrackedClick = (event: Event) => {
    const target = event.target instanceof Element ? event.target : null;
    const activity = target?.closest<HTMLElement>('.valid-activity[data-verbid]');
    const verbId = activity?.getAttribute('data-verbid')?.trim();
    if (!activity || !verbId || !isLearnerRecordVerb(verbId)) return;
    const trackedActivity = activity;
    this.log(verbId, {
      activityId:
        trackedActivity.getAttribute('data-activity-id') ??
        trackedActivity.getAttribute('data-item-id'),
      objectName:
        trackedActivity.getAttribute('aria-label') ?? trackedActivity.textContent?.trim() ?? null,
    });
  };

  private readonly onVisibilityChange = () => {
    if (this.document?.visibilityState === 'hidden') void this.flush({ preferBeacon: true });
  };

  private readonly onPageHide = () => void this.flush({ preferBeacon: true });

  private readonly onResourceLoad = (event: Event) =>
    this.logContentResource('CD-FGD', event);

  private readonly onResourceError = (event: Event) =>
    this.logContentResource('CD-ERR', event);

  start(): void {
    // Kill-switch: when bulk logging is disabled, skip DOM/content listeners so
    // no events are ever captured. log()/logSystem() short-circuit too.
    if (!environment.enableBulkLogging) return;
    if (this.started) return;
    this.started = true;
    this.document?.addEventListener('click', this.onTrackedClick, true);
    this.document?.addEventListener('load', this.onResourceLoad, true);
    this.document?.addEventListener('error', this.onResourceError, true);
    this.document?.addEventListener('visibilitychange', this.onVisibilityChange);
    globalThis.addEventListener?.('pagehide', this.onPageHide);
  }

  log(verbId: string, options: BulkLogOptions = {}): void {
    if (!environment.enableBulkLogging) return;
    const cleanVerbId = String(verbId ?? '').trim();
    if (!cleanVerbId || !isKnownLegacyVerb(cleanVerbId) || this.isAdminUser()) return;

    const timestamp = coerceDate(options.eventTimeStamp);
    const baseActivityId = this.resolveActivityId(cleanVerbId, options);
    if (cleanVerbId === 'CS') {
      // readSession() returns '' (not null) when the key is absent, so use ||
      // (not ??) to actually fall back to the current timestamp / passed value.
      this.pushEntry('LI', {
        ...options,
        activityId: baseActivityId,
        eventTimeStamp: this.readSession('loginCurrEventTimeStamp') || timestamp,
        verbValue: this.readSession('loginVerbVal') || options.verbValue || 0,
        otherKeysAndValues: this.loginOtherKeys(),
      });
    }

    if (!(cleanVerbId === 'CS' && this.isStudentUser())) {
      this.pushEntry(cleanVerbId, {
        ...options,
        activityId: baseActivityId,
        eventTimeStamp: timestamp,
      });
    }

    if (cleanVerbId === 'LO') {
      this.clearTimer();
      void this.flush();
    } else {
      this.ensureTimer();
    }
  }

  logSystem(verbId: string, options: BulkLogOptions = {}): void {
    this.log(verbId, { ...options, eventType: EVENT_TYPE_SYS });
  }

  async flush(options: { readonly preferBeacon?: boolean } = {}): Promise<void> {
    if (this.buffer.length === 0) return;
    // Identity may not be resolved yet (logs fired before auth/class context is
    // ready). Keep the entries buffered and retry on a later flush rather than
    // sending user-less rows; batch() backfills the caller user and class ids.
    if (!this.userId()) return;
    if (this.flushInFlight) {
      const previousSucceeded = await this.flushInFlight;
      if (!previousSucceeded || this.buffer.length === 0) return;
    }

    const entries = this.buffer.splice(0, this.buffer.length);
    const batch = this.batch(entries);
    if (options.preferBeacon) {
      // Unload-safe path (visibilitychange:hidden / pagehide). Each adapter picks its
      // own channel that survives the page unload.
      if (this.api.flushUnloadSafe(batch)) return;
      // Fall through to the normal POST below if the unload-safe path was unavailable.
    }

    this.flushInFlight = this.api
      .flush(batch)
      .then(() => true)
      .catch((err: unknown) => {
        this.buffer.unshift(...entries);
        console.warn('[bulk-log] failed to flush activity logs', err);
        return false;
      })
      .finally(() => {
        this.flushInFlight = null;
      });

    const succeeded = await this.flushInFlight;
    if (succeeded && this.buffer.length > 0) await this.flush();
  }

  private pushEntry(verbId: string, options: BulkLogOptions): void {
    const timestamp = coerceDate(options.eventTimeStamp);
    // Buffer even if identity isn't resolved yet - the caller user and class ids are
    // backfilled at flush time (flush() won't send until userId resolves). This
    // prevents losing events that fire moments before auth/class context lands.
    const entry: BulkLogEntry = {
      verbValue: stringifyValue(options.verbValue),
      activityId: cleanString(options.activityId),
      eventTimeStamp: formatLegacyTimestamp(timestamp),
      callerUserId: this.userId(),
      eventType: cleanString(options.eventType) || EVENT_TYPE_LRS,
      verbId,
      callerClassId: this.classId(),
      otherKeysAndValues: cleanString(options.otherKeysAndValues),
    };
    this.buffer.push({
      entry,
      timestamp,
      objectName: cleanString(options.objectName) || verbId,
    });
  }

  private batch(entries: readonly PendingBulkLog[]): BulkLogBatch {
    // Backfill identity for entries buffered before auth/class context resolved.
    const userId = this.userId();
    const classId = this.classId();
    return {
      callerAppType: CALLER_APP_TYPE_WEB_CLIENT,
      logDetails: entries.map(({ entry }) => ({
        ...entry,
        callerUserId: entry.callerUserId || userId,
        callerClassId: entry.callerClassId || classId,
      })),
    };
  }

  private resolveActivityId(verbId: string, options: BulkLogOptions): string {
    const explicit = cleanString(options.activityId);
    if (explicit) return explicit;
    if (verbId === 'LO') return '';

    const currentTab = this.readSession('currentTab').toLowerCase();
    if (currentTab === 'lessons') {
      const lessonItemId = this.sessionStorage.getJson<{ itemId?: unknown }>('lessonInfoData')
        ?.itemId;
      if (typeof lessonItemId === 'string' && lessonItemId.trim()) return lessonItemId.trim();
    }
    if (currentTab === 'library') {
      const bookId = this.readSession('BookID');
      if (bookId) return bookId;
    }
    if (verbId === 'IL') {
      const type = cleanString(options.type).toLowerCase();
      const bookId = this.readSession('BookID');
      const studentAssignId = this.readSession('StudentAssignID');
      if ((type === 'ebook' || type === 'pdf') && bookId) return bookId;
      if (type && studentAssignId) return studentAssignId;
    }
    if ((verbId === 'S-PKT-SCS' || verbId === 'T-PKT-SCS') && this.readSession('StudentAssignID')) {
      return this.readSession('StudentAssignID');
    }
    return '';
  }

  private ensureTimer(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
  }

  private clearTimer(): void {
    if (this.flushTimer === null) return;
    clearInterval(this.flushTimer);
    this.flushTimer = null;
  }

  private loginOtherKeys(): string {
    const appZipRevision = this.readLocal('AppZipRevisionNo') || '1';
    return `AppVersionNo:${environment.appVersion}, AppZipRevisionNo:${appZipRevision}`;
  }

  private userId(): string {
    return this.legacyAuth.getAuthContext().userId ?? this.auth.userId() ?? this.readSession('userID');
  }

  private classId(): string {
    return this.classStore.selectedClassId() ?? this.readSession('classID');
  }

  private isStudentUser(): boolean {
    const role = this.currentRole();
    return role === 'S' || role === 'STUDENT';
  }

  private isAdminUser(): boolean {
    return ADMIN_ROLES.has(this.currentRole());
  }

  private logContentResource(verbId: 'CD-FGD' | 'CD-ERR', event: Event): void {
    const target = event.target instanceof Element ? event.target : null;
    const url = resourceUrl(target);
    if (!url || !this.isTrackedContentUrl(url)) return;
    if (verbId === 'CD-FGD' && !isForegroundContentResource(target, url)) return;
    this.logSystem(verbId, {
      activityId: resourceActivityId(url),
      verbValue: resourceDurationMs(url),
      otherKeysAndValues: `URL:${url}`,
      objectName: 'Content download',
    });
  }

  private isTrackedContentUrl(url: string): boolean {
    const normalized = absoluteUrl(url, this.document);
    const configuredRoots = [
      this.routing.contentBaseUrl,
      this.readSession('contentBaseURL'),
      this.readSession('libraryPath'),
    ]
      .map((root) => absoluteUrl(root, this.document))
      .filter(Boolean);
    return (
      normalized.includes('/content/') ||
      configuredRoots.some((root) => normalized.startsWith(root))
    );
  }

  private currentRole(): string {
    return (
      this.classStore.userRoleInClass() ||
      this.readSession('userRoleInClass') ||
      this.readSession('userRole') ||
      this.userStore.role()
    )
      .trim()
      .toUpperCase();
  }

  // Trimmed on purpose: `currentTab` is string-compared, and the `userRoleInClass` ->
  // `userRole` -> store fallback chain would stop early on a whitespace-only value.
  private readSession(key: string): string {
    return toTrimmedString(this.sessionStorage.get(key));
  }

  private readLocal(key: string): string {
    return toTrimmedString(this.localStorage.get(key));
  }
}

function coerceDate(value: Date | string | number | null | undefined): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string' && value.trim()) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return new Date(parsed);
  }
  return new Date();
}

function formatLegacyTimestamp(date: Date): string {
  const day = pad2(date.getDate());
  const month = pad2(date.getMonth() + 1);
  return `${day}-${month}-${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(
    date.getSeconds(),
  )}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function stringifyValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '0';
  return String(value);
}

function cleanString(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function resourceUrl(target: Element | null): string {
  if (!target) return '';
  const element = target as Element & {
    readonly src?: string;
    readonly currentSrc?: string;
    readonly href?: string;
  };
  return element.currentSrc || element.src || element.href || '';
}

function isForegroundContentResource(target: Element | null, url: string): boolean {
  const tagName = target?.tagName.toLowerCase() ?? '';
  if (tagName === 'script') return true;
  return url.split('?')[0].split('#')[0].toLowerCase().endsWith('.js');
}

function absoluteUrl(value: string, documentRef: Document | null | undefined): string {
  if (!value) return '';
  try {
    return new URL(value, documentRef?.baseURI ?? globalThis.location?.href).href;
  } catch {
    return value;
  }
}

function resourceActivityId(url: string): string {
  const clean = url.split('?')[0].split('#')[0];
  const file = clean.substring(clean.lastIndexOf('/') + 1);
  return file.replace(/\.[^.]+$/, '') || clean;
}

function resourceDurationMs(url: string): number {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByName !== 'function') {
    return 0;
  }
  const entries = performance.getEntriesByName(url);
  const latest = entries[entries.length - 1] as PerformanceResourceTiming | undefined;
  return latest ? Math.max(0, Math.round(latest.duration)) : 0;
}
